import { useCallback, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import {
  useBreakpointHelpers,
  useNoticeBannersStackContext,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';
import { getUrqlError } from '@order/utils';

import { useActiveRouteName } from '../../navigation';
import { useFeatureFlaggedSubmitPromoOrGiftCardCode } from './useFeatureFlaggedSubmitPromoOrGiftCardCode';
import { usePromoCodeTelemetry } from './usePromoCodeTelemetry';

export const usePromoCode = (refetchCredits?: () => void) => {
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { t } = useLocalizationContext();
  const { isMobile } = useBreakpointHelpers();
  const navigation = useNavigation();
  const [promoCode, setPromoCode] = useState('');
  const [promoCodePreviouslyEntered, setPromoCodePreviouslyEntered] =
    useState(false);
  const [promoCodeInvalid, setPromoCodeInvalid] = useState(false);
  const routeName = useActiveRouteName();

  const {
    response: submitCodeResponse,
    submitPromoOrGiftCardCode: submitCode,
  } = useFeatureFlaggedSubmitPromoOrGiftCardCode();
  const { fetching: isSubmittingPromoCode } = submitCodeResponse;

  const {
    monitorPromoCodeApplied,
    monitorPromoCodeFailed,
    monitorPromoCodeEntered,
    monitorPromoCodeAttemptApply,
  } = usePromoCodeTelemetry();

  const handleSetPromoCode = useCallback(
    (input: string) => {
      setPromoCode(input);
      setPromoCodeInvalid(false);

      // Trigger promo code entered event once per session
      if (input.length > 0 && !promoCodePreviouslyEntered) {
        setPromoCodePreviouslyEntered(true);
        monitorPromoCodeEntered(routeName);
      }
    },
    [monitorPromoCodeEntered, promoCodePreviouslyEntered, routeName],
  );

  const submitPromoCode = useCallback(async () => {
    monitorPromoCodeAttemptApply(routeName);
    const { isSubmitSuccess, typename, error } = await submitCode(promoCode);

    setPromoCodeInvalid(!isSubmitSuccess);

    if (!isSubmitSuccess) {
      monitorPromoCodeFailed(
        t('account.notice.credit-invalid'),
        getUrqlError(error),
        routeName,
      );

      return;
    }

    setPromoCode('');
    setPromoCodePreviouslyEntered(false);
    monitorPromoCodeApplied(`${typename}`, routeName);

    if (isMobile && navigation.canGoBack()) {
      navigation.goBack();
    }

    addNoticeBanner({
      text: t(
        typename === 'SubmitPromoCodeSuccessV2'
          ? 'account.notice.promo-applied'
          : 'account.notice.credit-applied',
      ),
      palette: 'success',
    });

    refetchCredits?.();
  }, [
    promoCode,
    isMobile,
    navigation,
    routeName,
    t,
    addNoticeBanner,
    monitorPromoCodeFailed,
    monitorPromoCodeApplied,
    monitorPromoCodeAttemptApply,
    refetchCredits,
    submitCode,
  ]);

  return {
    promoCode,
    isSubmittingPromoCode,
    promoCodeInvalid,
    setPromoCode: handleSetPromoCode,
    submitPromoCode,
  };
};
